<script setup lang="ts">
import { mdiChevronDown } from "@mdi/js";
import {
	AccordionContent,
	AccordionHeader,
	AccordionItem,
	AccordionTrigger,
	injectAccordionRootContext,
	useForwardProps,
	useId,
	type AccordionItemProps,
} from "reka-ui";
import { computed, onMounted } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import Icon from "../icon/Icon.vue";

const id = useId(undefined, "accordion-item");

interface Props extends ClassProps, Omit<AccordionItemProps, "value"> {
	defaultOpen?: boolean;
	value?: string;
}

const props = defineProps<Props>();

defineSlots<{
	trigger(slotProps: { open: boolean }): void;
	chevron(slotProps: { open: boolean }): void;
	default(slotProps: { open: boolean }): void;
}>();

const forwarded = useForwardProps(props);

const accordionCtx = injectAccordionRootContext();

const value = computed(() => {
	if (props.value != null) {
		return props.value;
	}

	return id;
});

onMounted(async () => {
	if (props.defaultOpen) {
		accordionCtx.changeModelValue(value.value);
	}
});
</script>

<template>
	<AccordionItem
		v-slot="{ open }"
		:class="cn('border-b last:border-b-0', props.class)"
		:value="forwarded.value ?? id"
	>
		<AccordionHeader class="flex min-h-10">
			<AccordionTrigger
				class="group flex flex-1 items-center justify-between gap-4 disabled:pointer-events-none disabled:opacity-50"
			>
				<slot name="trigger" :open="open" />

				<slot name="chevron" :open="open">
					<Icon
						class="shrink-0 transition-transform group-data-[state=open]:rotate-180"
						:d="mdiChevronDown"
					/>
				</slot>
			</AccordionTrigger>
		</AccordionHeader>

		<AccordionContent
			class="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden"
		>
			<div class="pb-2 pt-0">
				<slot :open="open" />
			</div>
		</AccordionContent>
	</AccordionItem>
</template>
